<div class="grok-dialog" role="dialog" aria-modal="true" aria-labelledby="grok-title">
  <header class="dialog-header">
    <div class="title-wrap">
      <h2 id="grok-title">Grok 2 Image Gen</h2>
      <div class="chips">
        <span class="chip chip-type">Photoreal • Stylized • Fast Batching</span>
      </div>
    </div>

    <button class="close-btn" type="button" aria-label="Close dialog" (click)="close()">
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </button>
  </header>

  <section class="hero">
    <div class="hero-card">
      <div class="hero-copy">
        <h3>Why choose Grok 2 Image Gen?</h3>
        <p>
          <strong>Grok 2 Image Gen</strong> is built for high-quality visuals with strong prompt adherence,
          clean composition, and fast iteration. It supports multi-image batches and returns a helpful
          <em>revised prompt</em> so you can understand how the system interpreted your request.
        </p>
        <ul class="highlights">
          <li>
            <span class="icon" aria-hidden="true">⚡</span>
            Quick multi-image generation for rapid ideation (up to 10 per request)
          </li>
          <li>
            <span class="icon" aria-hidden="true">🎯</span>
            Reliable prompt following with coherent subjects and scenes
          </li>
          <li>
            <span class="icon" aria-hidden="true">🧠</span>
            Returns a revised_prompt to fine-tune your next iteration
          </li>
          <li>
            <span class="icon" aria-hidden="true">🖼️</span>
            JPG output via URL or Base64—easy to display or store
          </li>
        </ul>
      </div>

      <div class="hero-side">
        <div class="stat">
          <div class="stat-value">1–10</div>
          <div class="stat-label">Images per request</div>
        </div>
        <div class="stat">
          <div class="stat-value">JPG</div>
          <div class="stat-label">Output format</div>
        </div>
        <div class="stat">
          <div class="stat-value">Revised</div>
          <div class="stat-label">Prompt feedback</div>
        </div>
      </div>
    </div>
  </section>

  <section class="grid">
    <article class="card">
      <h4>Best for</h4>
      <ul class="bullets">
        <li>Concept boards & A/B exploration</li>
        <li>Product mockups & hero shots</li>
        <li>Editorial & lifestyle scenes</li>
        <li>Stylized illustrations and posters</li>
        <li>Marketing & social creatives</li>
      </ul>
    </article>

    <article class="card">
      <h4>When to pick this model</h4>
      <p class="muted">
        Choose <strong>Grok 2 Image Gen</strong> when you want fast, batchable concepts with strong prompt
        alignment and the ability to iterate quickly using the model’s revised prompt feedback.
      </p>
      <ul class="checks">
        <li>Need multiple candidates in one go</li>
        <li>Clear art direction (camera, lighting, mood)</li>
        <li>Photoreal or stylized visuals with consistent composition</li>
      </ul>
    </article>

    <article class="card">
      <h4>Tips for great results</h4>
      <ol class="tips">
        <li><strong>Be specific:</strong> subject, style, lens, lighting, background, palette.</li>
        <li><strong>Direct the shot:</strong> angle, framing, depth of field, negative space.</li>
        <li><strong>Style cues:</strong> “cinematic color grade”, “studio softbox”, “isometric”.</li>
        <li><strong>Negatives:</strong> add “no watermark, no text, no logo” if needed.</li>
      </ol>
    </article>

    <article class="card">
      <h4>Suggested settings (Clarichat)</h4>
      <div class="kv">
        <div><span>Model key</span><code>{{ modelId }}</code></div>
        <div><span>API model</span><code>{{ apiModelId }}</code></div>
        <div><span>response_format</span><code>url • b64_json</code></div>
        <div><span>n</span><code>1–10</code></div>
        <div><span>revised_prompt</span><code>Returned in response</code></div>
      </div>
      <div class="cta-row">
        <button class="btn primary" type="button" (click)="useModel()">Use this model</button>
        <button class="btn ghost" type="button" (click)="close()">Close</button>
      </div>
    </article>
  </section>

  <section class="examples">
    <h4>Example prompts</h4>
    <div class="code-grid">
      <pre class="code"><code>“Cinematic portrait of a traveler on a rain-soaked street at night, neon reflections, 85mm lens, shallow depth of field, realistic skin, no watermark”</code></pre>
      <pre class="code"><code>“Clean product render of a matte ceramic mug on a linen cloth, soft window light, neutral palette, subtle shadows, e-commerce hero image, no text”</code></pre>
      <pre class="code"><code>“Graphic poster in retro-futurist style, bold vector shapes, limited color palette, grain texture, centered composition, no logo”</code></pre>
    </div>
  </section>

</div>
